import { Box } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import { useTranslation } from "@outschool/localization";
import {
  ToggleButton,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import ldDifference from "lodash/difference";
import ldUnion from "lodash/union";
import React from "react";

export default function DayOfWeekSelector({
  value,
  onChange = (_dow: string) => {},
  showGroupSelection = false,
  daysSelected = SearchFilters.DAYS_OF_WEEK,
  isDisabled = false,
}: {
  value?: string | null;
  onChange: (dow: string) => void;
  showGroupSelection?: boolean;
  daysSelected?: string[];
  isDisabled?: boolean;
}) {
  const [impressionTrackingNode, setImpressionTrackingNode] =
    React.useState(null);
  const { trackTouch } = useImpressionTracking({
    node: impressionTrackingNode,
    trackingLabel: "dow_selector",
    uniqueId: "dow_selector",
  });

  const daysOfWeek = getDaysOfWeek(value);

  const setDays = (newDays: string[]) => {
    const newDaysOrdered = SearchFilters.DAYS_OF_WEEK.filter(
      d => newDays.indexOf(d) >= 0,
    );

    onChange(newDaysOrdered.join(","));
  };

  const onClick = (day: string, toggleEverything: boolean) => {
    trackTouch({
      selector: day,
      toggleEverything,
    });
    let newDays;
    if (toggleEverything) {
      newDays = ldDifference(SearchFilters.DAYS_OF_WEEK, daysOfWeek);
    } else {
      newDays = daysOfWeek.includes(day)
        ? ldDifference(daysOfWeek, [day])
        : ldUnion(daysOfWeek, [day]);
    }
    setDays(newDays);
  };

  const onGroupClick = (label: string, days: string[]) => {
    trackTouch({
      selector: label,
    });
    const isSelected = days.every(day => daysOfWeek.includes(day));
    const newDays = isSelected
      ? ldDifference(daysOfWeek, days)
      : ldUnion(daysOfWeek, days);
    setDays(newDays);
  };

  const { t } = useTranslation(
    "ui-components-website\\src\\Search\\DayOfWeekSelector",
  );
  const shouldDisableDay = (day: string) =>
    daysSelected.every(daySelected => day !== daySelected);

  return (
    <Box ref={setImpressionTrackingNode}>
      <Box
        flex
        sx={{
          marginLeft: "4px",
          marginBottom: showGroupSelection ? "0.5em" : 0,
        }}
      >
        {SearchFilters.DAYS_OF_WEEK.map(d => {
          return (
            <ToggleButton
              key={`dow-${d}`}
              enabled={daysOfWeek.indexOf(d) >= 0}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                onClick(d, e.shiftKey)
              }
              sx={{
                marginRight: "tiny",
                padding: "0.9rem",
                width: "14%",
                maxWidth: "5rem",
                textAlign: "center",
              }}
              disabled={shouldDisableDay(d) || isDisabled}
            >
              {SearchFilters.formatDayOfWeekShort(d)}
            </ToggleButton>
          );
        })}
      </Box>
      {showGroupSelection ? (
        <Box flex sx={{ marginLeft: "4px" }}>
          <ToggleButton
            enabled={SearchFilters.WEEKDAYS.every(day =>
              daysOfWeek.includes(day),
            )}
            onClick={() => onGroupClick("Weekdays", SearchFilters.WEEKDAYS)}
            sx={{
              marginRight: "tiny",
              height: "3.65rem",
              padding: "0.3rem 1.825rem",
              borderRadius: "1.825rem",
            }}
            disabled={
              SearchFilters.WEEKDAYS.every(weekday =>
                shouldDisableDay(weekday),
              ) || isDisabled
            }
          >
            {t`Weekdays`}
          </ToggleButton>
          <ToggleButton
            enabled={SearchFilters.WEEKEND_DAYS.every(day =>
              daysOfWeek.includes(day),
            )}
            disabled={
              SearchFilters.WEEKEND_DAYS.every(day => shouldDisableDay(day)) ||
              isDisabled
            }
            onClick={() => onGroupClick("Weekends", SearchFilters.WEEKEND_DAYS)}
            sx={{
              marginRight: "tiny",
              height: "3.65rem",
              padding: "0.3rem 1.825rem",
              borderRadius: "1.825rem",
            }}
          >
            {t`Weekends`}
          </ToggleButton>
        </Box>
      ) : null}
    </Box>
  );
}

function getDaysOfWeek(dow?: string | null) {
  return dow
    ? dow
        .trim()
        .split(",")
        .filter(d => d !== "")
    : [];
}
